
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProjectProvider } from "@/contexts/ProjectContext";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect } from "react";
import Dashboard from "./pages/Dashboard";
import Projects from "./pages/Projects";
import NewProject from "./pages/NewProject";
import Reports from "./pages/Reports";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";
import StageDetail from "./pages/StageDetail";
import ProjectSelector from "./pages/ProjectSelector";
import RiskDetail from "./pages/RiskDetail";
import NewRisk from "./pages/NewRisk";
import RisksPage from "./pages/RisksPage";
import ActivitiesPage from "./pages/ActivitiesPage";
import GanttChart from "./pages/GanttChart";
import CalendarView from "./pages/CalendarView";
import InformationExchange from "./pages/InformationExchange";
import Governance from "./pages/Governance";
import Procurement from "./pages/Procurement";
import AuditLog from "./pages/AuditLog";
import ExportReports from "./pages/ExportReports";
import Index from "./pages/Index";
import Ask from "./pages/Ask";
import KnowledgeCentre from "./pages/KnowledgeCentre";
import TrainingHub from "./pages/TrainingHub";
import BusinessCaseDeveloper from "./pages/BusinessCaseDeveloper";
import OptionsAppraisal from "./pages/OptionsAppraisal";
import SWOTAnalysis from "./pages/SWOTAnalysis";
import QualitativeAssessment from "./pages/QualitativeAssessment";
import TrainingModule from "./pages/TrainingModule";
import EconomicAssessment from "./pages/EconomicAssessment";
import FinancialAssessment from "./pages/FinancialAssessment";
import MinutesTracker from "./pages/MinutesTracker";
import CapitalProgramme from "./pages/CapitalProgramme";
import ProgressReporting from "./pages/ProgressReporting";
import ComplianceTracking from "./pages/ComplianceTracking";
import CostTracking from "./pages/CostTracking";
import RiskTracker from "./pages/RiskTracker";
import SustainabilityTracker from "./pages/SustainabilityTracker";
import { Loader2 } from "lucide-react";

// Create a more robust ProtectedRoute component for authenticated routes
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  useEffect(() => {
    console.log("ProtectedRoute: Authentication check, authenticated:", isAuthenticated, "loading:", isLoading);
  }, [isAuthenticated, isLoading]);
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <Loader2 className="h-10 w-10 text-primary animate-spin mb-4" />
        <p className="text-gray-500">Verifying authentication...</p>
        <p className="text-gray-400 text-sm mt-2">Please wait while we check your session</p>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    console.log("ProtectedRoute: User not authenticated, redirecting to login");
    return <Navigate to="/login" replace />;
  }
  
  console.log("ProtectedRoute: User authenticated, rendering protected content");
  return <>{children}</>;
};

// Handle redirect from 404.html if exists
const HandleRedirect = () => {
  useEffect(() => {
    const redirect = sessionStorage.getItem('redirect');
    if (redirect) {
      sessionStorage.removeItem('redirect');
      window.history.replaceState(null, '', redirect);
      console.log("HandleRedirect: Redirected from 404.html to:", redirect);
    } else {
      console.log("HandleRedirect: No redirect found in sessionStorage");
    }
  }, []);
  
  return null;
};

// Configure React Query with better error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const App = () => {
  console.log("App component rendering");
  
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <ProjectProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <HandleRedirect />
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/" element={<Index />} />
                
                {/* Protected routes */}
                <Route path="/projects-selector" element={
                  <ProtectedRoute>
                    <ProjectSelector />
                  </ProtectedRoute>
                } />
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/projects" element={
                  <ProtectedRoute>
                    <Projects />
                  </ProtectedRoute>
                } />
                <Route path="/new-project" element={
                  <ProtectedRoute>
                    <NewProject />
                  </ProtectedRoute>
                } />
                <Route path="/reports" element={
                  <ProtectedRoute>
                    <Reports />
                  </ProtectedRoute>
                } />
                <Route path="/export" element={
                  <ProtectedRoute>
                    <ExportReports />
                  </ProtectedRoute>
                } />
                <Route path="/stage/:stageId" element={
                  <ProtectedRoute>
                    <StageDetail />
                  </ProtectedRoute>
                } />
                <Route path="/risks/:riskId" element={
                  <ProtectedRoute>
                    <RiskDetail />
                  </ProtectedRoute>
                } />
                <Route path="/risks/new" element={
                  <ProtectedRoute>
                    <NewRisk />
                  </ProtectedRoute>
                } />
                <Route path="/risks" element={
                  <ProtectedRoute>
                    <RisksPage />
                  </ProtectedRoute>
                } />
                <Route path="/activities" element={
                  <ProtectedRoute>
                    <ActivitiesPage />
                  </ProtectedRoute>
                } />
                <Route path="/gantt" element={
                  <ProtectedRoute>
                    <GanttChart />
                  </ProtectedRoute>
                } />
                <Route path="/calendar" element={
                  <ProtectedRoute>
                    <CalendarView />
                  </ProtectedRoute>
                } />
                <Route path="/information-exchange" element={
                  <ProtectedRoute>
                    <InformationExchange />
                  </ProtectedRoute>
                } />
                <Route path="/governance" element={
                  <ProtectedRoute>
                    <Governance />
                  </ProtectedRoute>
                } />
                <Route path="/procurement" element={
                  <ProtectedRoute>
                    <Procurement />
                  </ProtectedRoute>
                } />
                <Route path="/audit-log" element={
                  <ProtectedRoute>
                    <AuditLog />
                  </ProtectedRoute>
                } />
                
                {/* Business Case Management Routes */}
                <Route path="/ask" element={
                  <ProtectedRoute>
                    <Ask />
                  </ProtectedRoute>
                } />
                <Route path="/knowledge-centre" element={
                  <ProtectedRoute>
                    <KnowledgeCentre />
                  </ProtectedRoute>
                } />
                <Route path="/training-hub" element={
                  <ProtectedRoute>
                    <TrainingHub />
                  </ProtectedRoute>
                } />
                <Route path="/business-case-developer" element={
                  <ProtectedRoute>
                    <BusinessCaseDeveloper />
                  </ProtectedRoute>
                } />
                
                {/* Consultancy Tools Routes */}
                <Route path="/options-appraisal" element={
                  <ProtectedRoute>
                    <OptionsAppraisal />
                  </ProtectedRoute>
                } />
                <Route path="/swot-analysis" element={
                  <ProtectedRoute>
                    <SWOTAnalysis />
                  </ProtectedRoute>
                } />
                <Route path="/qualitative-assessment" element={
                  <ProtectedRoute>
                    <QualitativeAssessment />
                  </ProtectedRoute>
                } />
                <Route path="/economic-assessment" element={
                  <ProtectedRoute>
                    <EconomicAssessment />
                  </ProtectedRoute>
                } />
                <Route path="/financial-assessment" element={
                  <ProtectedRoute>
                    <FinancialAssessment />
                  </ProtectedRoute>
                } />
                <Route path="/minutes-tracker" element={
                  <ProtectedRoute>
                    <MinutesTracker />
                  </ProtectedRoute>
                } />
                
                {/* Performance Routes */}
                <Route path="/progress-reporting" element={
                  <ProtectedRoute>
                    <ProgressReporting />
                  </ProtectedRoute>
                } />
                <Route path="/compliance-tracking" element={
                  <ProtectedRoute>
                    <ComplianceTracking />
                  </ProtectedRoute>
                } />
                <Route path="/cost-tracking" element={
                  <ProtectedRoute>
                    <CostTracking />
                  </ProtectedRoute>
                } />
                <Route path="/risk-tracker" element={
                  <ProtectedRoute>
                    <RiskTracker />
                  </ProtectedRoute>
                } />
                <Route path="/sustainability-tracker" element={
                  <ProtectedRoute>
                    <SustainabilityTracker />
                  </ProtectedRoute>
                } />
                
                {/* Capital Programme Route */}
                <Route path="/capital-programme" element={
                  <ProtectedRoute>
                    <CapitalProgramme />
                  </ProtectedRoute>
                } />
                
                <Route path="/training-module" element={
                  <ProtectedRoute>
                    <TrainingModule />
                  </ProtectedRoute>
                } />
                
                {/* Catch-all route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </ProjectProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
